import React, { useState, useEffect } from "react";
import { getSavedTweets, updateTweet } from "../../services/tweetService";
import { AtSymbolIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { useSearchParams, useLocation } from "react-router-dom";

const SavedPosts = ({ collapsed }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const openModal = (item) => {
    setEditingItem(item);
    setEditText(item.text);
    setIsModalOpen(true);
  };

  const [pagination, setPagination] = useState({
    pageSize: Number(searchParams.get("pageSize")) || 10,
    page: Number(searchParams.get("page")) || 1,
    totalItems: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    filter: searchParams.get("filter") || "all",
  });
  const [filters, setFilters] = useState({
    user_id: "",
  });

  const handleEditClick = (item) => {
    setEditText(item.text);
  };

  const shareToTwitter = (item) => {
    const text = encodeURIComponent(item.text);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  const shareToThreads = (item) => {
    const text = encodeURIComponent(item.text);
    window.open(`https://www.threads.net/intent/post?text=${text}`, "_blank");
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await updateTweet(editingItem.tweet_id, editText);

      const updated = items.map((i) =>
        i.tweet_id === editingItem.tweet_id ? { ...i, text: editText } : i,
      );

      setItems(updated);

      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Tweet updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Something went wrong while updating the tweet.",
      });
    } finally {
      // Always executed
      setIsSaving(false);
      setIsModalOpen(false);
    }
  };

  // const fetchData = async (page = 1, pageSize = 5) => {
  //   setIsLoading(true);
  //   try {
  //     // Build query string
  //     const queryParams = new URLSearchParams({
  //       page: page.toString(),
  //       pageSize: pageSize.toString(),
  //     });

  //     const response = await getSavedTweets(queryParams);
  //     setPagination(response.data.pagination);
  //     setItems(response.data.data);
  //     setError(null);
  //   } catch (err) {
  //     setError(err.message);
  //     setItems([]);
  //   } finally {
  //     setIsLoading(false); // ⬅️ Stop loader always
  //   }
  // };

  const fetchData = async ({
    page = pagination.page,
    pageSize = pagination.pageSize,
    filter = pagination.filter,
  } = {}) => {
    setIsLoading(true);

    try {
      // Build query string
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        filter: filter, // ← ADD FILTER HERE
      });

      const response = await getSavedTweets(queryParams);

      setPagination(response.data.pagination); // {page, pageSize, filter}
      setItems(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchData(newPage, pagination.pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    fetchData(1, newSize);
  };

  // Handle filter changes
  // const handleFilterChange = (e) => {
  //   const { name, value } = e.target;
  //   setFilters((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleFilterChange = (e) => {
    const value = e.target.value;

    setPagination((prev) => ({
      ...prev,
      filter: value,
      page: 1, // reset to page 1 when filter changes
    }));

    // Update URL query param
    const params = new URLSearchParams(window.location.search);
    params.set("filter", value);
    window.history.replaceState(
      {},
      "",
      `${location.pathname}?${params.toString()}`,
    );

    // Fetch data again using the new filter
    fetchData({ ...pagination, filter: value, page: 1 });
  };

  // Apply filters
  const applyFilters = () => {
    fetchData(1, pagination.pageSize);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      user_id: "",
      is_approved: "",
    });
    fetchData(1, pagination.pageSize);
  };

  return (
    <div
      className={`transition-all duration-300 ${
        collapsed ? "lg:ml-16" : "lg:ml-72"
      }`}
    >
      <h1>Generated Tweets or Posts</h1>

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-700 text-lg font-medium">
              Processing Please Wait...
            </p>
          </div>
        </div>
      )}

      {/* Controls and Stats Section */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-6 bg-white p-4 rounded-lg shadow">
        <div className="flex items-center mb-4 md:mb-0">
          <label
            htmlFor="pageSize"
            className="mr-3 text-sm font-medium text-gray-700"
          >
            Items per page:
          </label>
          <select
            id="pageSize"
            value={pagination.pageSize}
            onChange={handlePageSizeChange}
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <label
            htmlFor="filterVal"
            className="ml-4 mr-3 text-sm font-medium text-gray-700"
          >
            Filter:
          </label>

          <select
            id="filterVal"
            value={pagination.filter}
            onChange={handleFilterChange}
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="new">Newly Generated</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded">
          Showing{" "}
          <span className="font-semibold">
            {(pagination.page - 1) * pagination.pageSize + 1}
          </span>{" "}
          to{" "}
          <span className="font-semibold">
            {Math.min(
              pagination.page * pagination.pageSize,
              pagination.totalItems,
            )}
          </span>{" "}
          of <span className="font-semibold">{pagination.totalItems}</span>{" "}
          tweets
        </div>
      </div>

      <ul role="list" className="divide-y divide-gray-100">
        {items.map((item) => (
          <li
            key={item.tweet_id}
            className="flex items-center justify-between gap-x-6 py-5"
          >
            <div className="min-w-0">
              <div className="flex items-start gap-x-3">
                <p className="text-sm/6 font-semibold text-gray-900">
                  {item.text}
                </p>
              </div>
              <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                <p className="whitespace-nowrap">
                  Created on{" "}
                  <time dateTime={item.generation_time}>
                    {item.generation_time}
                  </time>
                </p>
              </div>
            </div>
            {/* <div className="flex flex-none items-center gap-x-4">
              <button
                onClick={() => openModal(item)}
                className="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
              >
                Edit
              </button>
              <a
                href="test.com"
                className="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
              >
                Post
              </a>
            </div> */}

            <div className="flex flex-none items-center gap-x-4">
              {/* EDIT BUTTON */}
              <button
                onClick={() => openModal(item)}
                className="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold 
               text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 
               hover:bg-gray-50 sm:block"
              >
                Edit
              </button>

              {/* SHARE TO TWITTER */}
              <button
                onClick={() => shareToTwitter(item)}
                className="hidden rounded-md bg-blue-500 px-2.5 py-1.5 text-sm font-semibold 
             text-white shadow-sm hover:bg-blue-600 sm:flex items-center gap-2"
              >
                {/* Bird Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="w-4 h-4"
                >
                  <path d="M22.46 6c-.77.35-1.5.57-2.24.67.82-.49 1.45-1.27 1.75-2.2-.8.46-1.64.8-2.52.98A4.17 4.17 0 0 0 16.1 4c-2.3 0-4.17 1.94-4.17 4.34 0 .34.03.68.1 1C8.03 9.2 4.8 7.38 2.67 4.6c-.37.7-.58 1.48-.58 2.32 0 1.6.76 3.02 1.93 3.85-.7 0-1.37-.23-1.95-.56v.06c0 2.24 1.47 4.12 3.42 4.54-.36.1-.75.14-1.15.14-.28 0-.55-.02-.81-.08.55 1.82 2.16 3.14 4.06 3.18A8.36 8.36 0 0 1 2 19.6a11.7 11.7 0 0 0 6.29 1.9c7.55 0 11.68-6.5 11.68-12.14v-.56c.8-.6 1.47-1.3 2.01-2.12z" />
                </svg>
                /{/* X Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  className="w-4 h-4"
                >
                  <path d="M18.36 2H21l-6.56 7.51L22 22h-5.52L12.4 14.6 7.73 22H3l7.1-8.13L2 2h5.64l4.32 6.53L18.36 2z" />
                </svg>
                Tweet
              </button>

              {/* SHARE TO THREADS */}
              <button
                onClick={() => shareToThreads(item)}
                className="hidden rounded-md bg-black px-2.5 py-1.5 text-sm font-semibold 
             text-white shadow-sm hover:bg-gray-800 sm:flex items-center gap-2"
              >
                <AtSymbolIcon className="w-4 h-4" />
                Threads
              </button>
            </div>
          </li>
        ))}
      </ul>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl shadow-2xl border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl font-semibold text-gray-900">
                Edit Tweet
              </h2>

              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>

            {/* Textarea */}
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-4 text-base h-48 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
              placeholder="Edit your tweet here..."
            />

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium 
              hover:bg-blue-700 transition flex items-center justify-center gap-2
              ${isSaving ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-lg shadow">
          <div className="mb-4 sm:mb-0">
            <nav className="flex space-x-2" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPrevious}
                className={`px-3 py-1 rounded-md ${
                  !pagination.hasPrevious
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                }`}
              >
                <span className="sr-only">First</span>
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className={`px-4 py-1 rounded-md ${
                  !pagination.hasPrevious
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                }`}
              >
                Previous
              </button>
            </nav>
          </div>

          <div className="hidden md:flex mb-4 sm:mb-0">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{pagination.page}</span> of{" "}
              <span className="font-medium">{pagination.totalPages}</span>
            </p>
          </div>

          <div className="flex space-x-2">
            {Array.from(
              { length: Math.min(5, pagination.totalPages) },
              (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-1 rounded-md ${
                      pagination.page === pageNum
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              },
            )}
          </div>

          <div className="mt-4 sm:mt-0">
            <nav className="flex space-x-2" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className={`px-4 py-1 rounded-md ${
                  !pagination.hasNext
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                }`}
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNext}
                className={`px-3 py-1 rounded-md ${
                  !pagination.hasNext
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                }`}
              >
                <span className="sr-only">Last</span>
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};
export default SavedPosts;
